import { useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card as DesignCard } from '@hivebrite/design-system/Card';
import { Image } from '@hivebrite/design-system/Image';
import { Tooltip } from '@hivebrite/design-system/Tooltip';

import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

const DefaultTile = ({ image, logo, title, brandBarColor }) => {
  const { flagEvGsMembershipLogo } = useFeatureFlag();
  const membershipTypeLogo =
    flagEvGsMembershipLogo && logo ? logo.thumbnail_url : image;

  return (
    <Tile brandBarColor={brandBarColor} p={2}>
      <Box alignItems="center">
        {membershipTypeLogo && (
          <Box
            alignItems="flex-start"
            width={{ _: '24px', l: '30px' }}
            mr={4}
            flexGrow={0}
            flexShrink={0}
          >
            <ImageWrapper>
              <Image
                src={membershipTypeLogo}
                alt={s__('Memberships|Membership picture')}
              />
            </ImageWrapper>
          </Box>
        )}
        <Box flexGrow={1} font="h3-semibold" minWidth={0}>
          <Title>{title}</Title>
        </Box>
      </Box>
    </Tile>
  );
};

const SmallTile = ({
  image,
  logo,
  title,
  brandBarColor,
  parent,
  maxWidth = 169,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const onFocus = () => setIsFocused(true);
  const onBlur = () => setIsFocused(false);
  const { flagEvGsMembershipLogo } = useFeatureFlag();
  const membershipTypeLogo =
    flagEvGsMembershipLogo && logo ? logo.thumbnail_url : image;

  return (
    <Tile
      brandBarColor={brandBarColor}
      borderWidth="6px"
      p={0}
      width="100%"
      maxWidth={maxWidth}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <Tooltip
        childrenElement={`${parent} - ${title}`}
        maxWidth={maxWidth}
        shouldRenderTooltip={isFocused}
      >
        <Box alignItems="center" height={32} pr={3} maxWidth={maxWidth}>
          {membershipTypeLogo && (
            <Box alignItems="flex-start" flexGrow={0} flexShrink={0} pl={2}>
              <ImageWrapper height="22px" width="22px">
                <Image
                  src={membershipTypeLogo}
                  alt={s__('Memberships|Membership picture')}
                  width="100%"
                />
              </ImageWrapper>
            </Box>
          )}
          <Box flexGrow={1} font="body-semibold-asphalt" pl={2} minWidth={0}>
            <Title>{title}</Title>
          </Box>
        </Box>
      </Tooltip>
    </Tile>
  );
};

const MembershipTile = ({ variant, ...props }) =>
  variant === 'xs' ? <SmallTile {...props} /> : <DefaultTile {...props} />;

export default MembershipTile;

const Tile = styled(DesignCard)
  .withConfig({
    shouldForwardProp: (prop) => !['brandBarColor'].includes(prop),
  })
  .attrs(() => ({
    width: '100%',
    tabIndex: 0,
  }))`
  border-left: ${({ borderWidth = '12px' }) => borderWidth} solid
  ${({ theme, brandBarColor }) => brandBarColor || theme.colors.primary};
`;

const ImageWrapper = styled(Box).attrs(({ height = 24, width = 24 } = {}) => ({
  height,
  width,
}))`
  border-radius: 50%;
  overflow: hidden;
`;

const Title = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
